import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useNavigate } from "react-router-dom";
import { License, EnrichedLicense } from "../../store/types";
import { useProjectsAPI } from "../../hooks/api/use-projectsAPI";
import {
  useLicensesAPI,
  buildLicensesEndpoint,
} from "../../hooks/api/use-licensesAPI";
import { useCompaniesAPI } from "../../hooks/api/use-companiesAPI";
import { useUsersAPI } from "../../hooks/api/use-usersAPI";
import { useGetQuery } from "../../store/slices/apiSlice";
import {
  enrichLicense,
  getLicenseStats,
  licenseSearchText,
  matchesLicenseStat,
} from "../../hooks/licenseHelpers";
import type { LicenseStatFilter } from "../../hooks/licenseHelpers";
import {
  Container,
  ContainerXScroll,
} from "../../components/Container/Container";
import CustomInputText from "../../components/CustomInput/CustomInputText";
import CustomDatatable from "../../components/CustomDatatable/CustomDatatable";
import type { DatatableHandle } from "../../components/CustomDatatable/CustomDatatable";
import StatsPanel from "../../components/StatsPanel/StatsPanel";
import type { StatItem } from "../../components/StatsPanel/StatsPanel";
import {
  projectLicenseColumns,
  getLicenseContextMenuItems,
  getLicenseMobileActions,
} from "../../components/DatatableTemplates/LicenseTableTemplates";
import AddLicenseDialogButton from "../../components/DialogButton/AddLicenseDialogButton";
import ProjectDialogs from "../../components/Dialogs/ProjectDialogs";
import LicenseDialogs from "../../components/Dialogs/LicenseDialogs";
import { StyledIconLine } from "../../components/StyledIcon/StyledIcon";
import { H2 } from "../../styles/Typography";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import {
  IconDocFilled,
  IconDocCheckFilled,
  IconDocWarnFilled,
  IconDocErrorFilled,
  IconArrowLeft,
  IconSearch,
  IconCaseFilled,
  IconUser,
  IconEdit,
  IconDelete,
} from "../../assets/icons/Icon";
import {
  ContainerFlex,
  ContainerColumnFlex,
  CardsColumnContainer,
  ButtonContainer,
  ButtonLabel,
  SearchContainer,
} from "./ProjectDetails.styles";
import {
  FlexLineJustify,
  FlexLineGapSmall,
  FilterComponents,
  PageName,
  Label,
  ButtonBox,
} from "../../styles/PageLayoutStyles.styles";
import { Spinner } from "../CRUDPages/CRUDPages.styles";

const ProjectDetails: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)") || false;

  const tableRef = useRef<DatatableHandle<EnrichedLicense>>(null);
  const [licenseSearchValue, setLicenseSearchValue] = useState<string>("");
  const [licenseList, setLiscenseList] = useState<EnrichedLicense[]>([]);
  const [totalLicenses, setTotalLicenses] = useState(0);
  const [activeLicenses, setActiveLicenses] = useState(0);
  const [expiringLicenses, setExpiringLicenses] = useState(0);
  const [inactiveLicenses, setInactiveLicenses] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState(0);

  /** Карточка статистики, по которой отфильтрована таблица лицензий */
  const [statFilter, setStatFilter] = useState<LicenseStatFilter>("all");

  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [projectDialogVisible, setProjectDialogVisible] =
    useState<boolean>(false);
  const [projectDialogType, setProjectDialogType] = useState<string>("");
  const [licenseDialogVisible, setLicenseDialogVisible] =
    useState<boolean>(false);
  const [licenseDialogType, setLicenseDialogType] = useState<string>("");

  const {
    projectsEP,
    handleUpdateProject,
    handleDeleteProject,
    toastRef: projectToastRef,
    refetchData,
  } = useProjectsAPI();
  const { companies, companiesDropdownOpts } = useCompaniesAPI();
  const {
    licensesEP,
    handlePostLicense,
    handleActivateLicense,
    handleDeactivateLicense,
    handleUpdateLicense,
    handleAssignSeat,
    handleRemoveSeat,
    handleDeleteLicense,
    toastRef,
  } = useLicensesAPI(undefined, { skipList: true });
  const { users } = useUsersAPI();

  const {
    data: project,
    refetch: refetchProject,
    isLoading,
  } = useGetQuery(`${projectsEP}/${projectId}`, {
    refetchOnMountOrArgChange: true,
    skip: !projectId || !projectsEP,
  });

  const {
    data: licenses,
    refetch: refetchLicenses,
    isLoading: isLicensesLoading,
  } = useGetQuery(buildLicensesEndpoint(licensesEP, { projectId }), {
    refetchOnMountOrArgChange: true,
    skip: !projectId || !licensesEP,
  });

  useEffect(() => {
    if (project && licenses) {
      setLiscenseList(
        licenses.map((license: License) =>
          enrichLicense(license, { companies, projectName: project?.name })
        )
      );

      const stats = getLicenseStats(licenses);
      setTotalLicenses(stats.total);
      setActiveLicenses(stats.active);
      setExpiringLicenses(stats.expiring);
      setInactiveLicenses(stats.inactive);
      setOnlineUsers(stats.online);
    } else {
      setLiscenseList([]);
      setTotalLicenses(0);
      setActiveLicenses(0);
      setExpiringLicenses(0);
      setInactiveLicenses(0);
      setOnlineUsers(0);
    }
  }, [project, licenses, companies]);

  const filteredData = useMemo(() => {
    if (!licenseSearchValue.trim() || !licenseList) return licenseList;
    const lowerSearch = licenseSearchValue.toLowerCase();
    return licenseList.filter((license) =>
      licenseSearchText(license).includes(lowerSearch)
    );
  }, [licenseList, licenseSearchValue]);

  const statItems: StatItem[] = useMemo(
    () => [
      {
        id: "all",
        icon: <IconDocFilled size={24} />,
        header: totalLicenses.toString(),
        title: "Количество лицензий",
      },
      {
        id: "active",
        icon: <IconDocCheckFilled size={24} />,
        header: activeLicenses.toString(),
        title: "Активные лицензии",
      },
      {
        id: "expiring",
        icon: <IconDocWarnFilled size={24} />,
        header: expiringLicenses.toString(),
        title: "Истекающие лицензии",
      },
      {
        id: "inactive",
        icon: <IconDocErrorFilled size={24} />,
        header: inactiveLicenses.toString(),
        title: "Неактивные лицензии",
      },
      {
        id: "online",
        icon: <IconUser size={24} />,
        header: onlineUsers.toString(),
        title: "Пользователей онлайн",
        wide: true,
      },
    ],
    [
      totalLicenses,
      activeLicenses,
      expiringLicenses,
      inactiveLicenses,
      onlineUsers,
    ]
  );

  /** Связь панели статистики с таблицей лицензий */
  const applyStatFilter = (filter: LicenseStatFilter) => {
    setStatFilter(filter);
    tableRef.current?.setRowFilter(
      "stat",
      filter === "all"
        ? null
        : (license) => matchesLicenseStat(license, filter)
    );
  };

  const handleStatSelect = (id: string) => {
    const filter = id as LicenseStatFilter;
    // повторный клик по той же карточке снимает фильтр
    applyStatFilter(statFilter === filter ? "all" : filter);
  };

  const handleEnableLicense = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=enable&id=${license.id}`);
    } else {
      setLicenseDialogType("enable");
      setLicenseDialogVisible(true);
    }
  };

  const handleDisableLicense = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=disable&id=${license.id}`);
    } else {
      setLicenseDialogType("disable");
      setLicenseDialogVisible(true);
    }
  };

  const handleAddUser = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=addUser&id=${license.id}`);
    } else {
      setLicenseDialogType("addUser");
      setLicenseDialogVisible(true);
    }
  };

  const editLicenseUser = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=editUser&id=${license.id}`);
    } else {
      setLicenseDialogType("editUser");
      setLicenseDialogVisible(true);
    }
  };

  const deleteLicenseUser = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=deleteUser&id=${license.id}`);
    } else {
      setLicenseDialogType("deleteUser");
      setLicenseDialogVisible(true);
    }
  };

  const contextMenuItems = useMemo(
    () =>
      getLicenseContextMenuItems(
        selectedLicense,
        handleAddUser,
        editLicenseUser,
        deleteLicenseUser,
        handleEnableLicense,
        handleDisableLicense
      ),
    [selectedLicense, handleAddUser, handleEnableLicense, handleDisableLicense]
  );

  const mobileActions = useMemo(
    () => getLicenseMobileActions(handleEnableLicense, handleDisableLicense),
    [handleEnableLicense, handleDisableLicense]
  );

  const handleViewLicense = (license: License) => {
    navigate(`/licenses/${license.id}`);
  };

  const handleSelectionChange = (selected: License | null) => {
    setSelectedLicense(selected);
  };

  const resetAllFilters = () => {
    tableRef.current?.reset();
    setLicenseSearchValue("");
    setStatFilter("all");
  };

  const editLicense = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=edit&id=${license.id}`);
    } else {
      setLicenseDialogType("edit");
      setLicenseDialogVisible(true);
    }
  };

  const deleteLicense = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=delete&id=${license.id}`);
    } else {
      setLicenseDialogType("delete");
      setLicenseDialogVisible(true);
    }
  };

  const editProject = () => {
    if (isMobile) {
      navigate(`/projects/form?mode=edit&id=${project.id}`);
    } else {
      setProjectDialogType("edit");
      setProjectDialogVisible(true);
    }
  };

  const deleteProject = () => {
    if (isMobile) {
      navigate(`/projects/form?mode=delete&id=${project.id}`);
    } else {
      setProjectDialogType("delete");
      setProjectDialogVisible(true);
    }
  };

  const deleteProjectAPI = async (): Promise<boolean> => {
    try {
      let success = await handleDeleteProject(project);
      if (success) {
        return true;
      }
      return false;
    } catch (error) {
      return false;
    } finally {
      setTimeout(() => {
        navigate(-1);
      }, 2000);
      refetchData();
    }
  };

  if (isLoading || !project)
    return (
      <Container>
        <Spinner>
          <ProgressSpinner />
          <p>Загрузка...</p>
        </Spinner>
      </Container>
    );

  return (
    <ContainerFlex>
      <Toast ref={toastRef} position="bottom-center" />
      <Toast ref={projectToastRef} position="bottom-center" />
      <ContainerColumnFlex>
        <CardsColumnContainer>
          <Container>
            <FlexLineJustify>
              <H2>Данные проекта</H2>
              <ButtonBox>
                <Button text onClick={editProject}>
                  <IconEdit size={16} />
                </Button>
                <Button text onClick={deleteProject}>
                  <IconDelete size={16} />
                </Button>
              </ButtonBox>
            </FlexLineJustify>
            <StyledIconLine
              icon={<IconCaseFilled size={20} />}
              text={project ? `${project.name}` : ""}
            />
          </Container>

          <StatsPanel
            items={statItems}
            selectedId={statFilter === "all" ? null : statFilter}
            onSelect={handleStatSelect}
          />
        </CardsColumnContainer>

        <ButtonContainer>
          <Button outlined onClick={() => navigate(-1)}>
            <IconArrowLeft size={16} />
            <ButtonLabel>Назад</ButtonLabel>
          </Button>
        </ButtonContainer>
      </ContainerColumnFlex>

      <ContainerXScroll>
        <FlexLineJustify>
          <PageName>
            <H2>Лицензии</H2>
          </PageName>
          <FlexLineGapSmall>
            <AddLicenseDialogButton
              project={project}
              companiesOptions={companiesDropdownOpts}
              handleActivateLicense={handleActivateLicense}
              handleDeactivateLicense={handleDeactivateLicense}
              handleAddLicense={handlePostLicense}
              handleEditLicense={handleUpdateLicense}
              handleAssignSeat={handleAssignSeat}
              handleDeleteLicense={handleDeleteLicense}
              refetchData={refetchLicenses}
              licensesEP={licensesEP}
              isMobile={isMobile}
            />
            <FilterComponents>
              <Button
                icon="pi pi-filter-slash"
                onClick={resetAllFilters}
                outlined
              >
                <Label>Сбросить</Label>
              </Button>
              <SearchContainer>
                <CustomInputText
                  value={licenseSearchValue}
                  setValue={setLicenseSearchValue}
                  placeholder="Поиск..."
                  icon={<IconSearch size={20} />}
                />
              </SearchContainer>
            </FilterComponents>
          </FlexLineGapSmall>
        </FlexLineJustify>
        <CustomDatatable<License>
          ref={tableRef}
          data={filteredData ?? []}
          columns={projectLicenseColumns}
          onView={handleViewLicense}
          onEdit={editLicense}
          onDelete={deleteLicense}
          onRowDoubleClick={handleViewLicense}
          selected={selectedLicense}
          onSelectionChange={handleSelectionChange}
          getRowId={(license) => license.id}
          extraContextMenuItems={contextMenuItems}
          hideViewButton={false}
          mobileActions={mobileActions}
          sortable
        />
        <LicenseDialogs
          visible={licenseDialogVisible}
          onHide={() => setLicenseDialogVisible(false)}
          dialogType={licenseDialogType}
          license={selectedLicense!}
          enrichedLicense={selectedLicense!}
          project={project}
          handleActivateLicense={handleActivateLicense}
          handleDeactivateLicense={handleDeactivateLicense}
          handleAddLicense={handlePostLicense}
          handleEditLicense={handleUpdateLicense}
          handleAssignSeat={handleAssignSeat}
          handleRemoveSeat={handleRemoveSeat}
          handleDeleteLicense={handleDeleteLicense}
          refetchData={refetchLicenses}
          companiesOptions={companiesDropdownOpts}
          usersList={users}
          licensesEP={licensesEP}
        />
      </ContainerXScroll>

      <ProjectDialogs
        visible={projectDialogVisible}
        onHide={() => setProjectDialogVisible(false)}
        dialogType={projectDialogType}
        project={project}
        handleEditProject={handleUpdateProject}
        handleDeleteProject={deleteProjectAPI}
        refetchData={refetchProject}
      />
    </ContainerFlex>
  );
};

export default ProjectDetails;
